import React from 'react';
import { Link } from 'react-router-dom';
interface ProductHeroProps {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryCta: {
    text: string;
    href: string;
  };
  secondaryCta?: {
    text: string;
    href: string;
  };
  gradient: 'copilot' | 'gtm-studio' | 'orchestration' | 'signals' | 'predictive' | 'platform' | 'plays' | 'playbooks' | 'pricing';
  image?: string;
  pattern?: 'dots' | 'grid' | 'waves';
}
const gradients = {
  copilot: 'linear-gradient(135deg, #0F172A 0%, #1F6FEB 100%)',
  'gtm-studio': 'linear-gradient(135deg, #0F172A 0%, #0F766E 100%)',
  orchestration: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
  signals: 'linear-gradient(135deg, #312E81 0%, #6366F1 100%)',
  predictive: 'linear-gradient(135deg, #F97316 0%, #FDE68A 100%)',
  platform: 'linear-gradient(135deg, #0F172A 0%, #DC2626 100%)',
  plays: 'linear-gradient(135deg, #F1F5F9 0%, #FEF9C3 100%)',
  playbooks: 'linear-gradient(135deg, #F1F5F9 0%, #EDE9FE 100%)',
  pricing: 'linear-gradient(135deg, #EEF2FF 0%, #FFFFFF 100%)'
};
export function ProductHero({
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  gradient,
  image,
  pattern = 'dots'
}: ProductHeroProps) {
  const isDark = !['plays', 'playbooks', 'pricing'].includes(gradient);
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const subtitleColor = isDark ? 'text-white/80' : 'text-gray-600';
  const eyebrowBg = isDark ? 'bg-white/10' : 'bg-gray-900/10';
  const eyebrowText = isDark ? 'text-white' : 'text-gray-900';
  return <section className="relative overflow-hidden" style={{
    background: gradients[gradient],
    minHeight: '520px',
    maxHeight: '560px'
  }} data-id="element-3666">
      {/* Pattern Overlay */}
      {pattern === 'dots' && <div className="absolute inset-0 opacity-10" data-id="element-3667">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.2),transparent_50%)]" data-id="element-3668" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.15),transparent_50%)]" data-id="element-3669" />
        </div>}
      {pattern === 'grid' && <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px] opacity-20" data-id="element-3670" />}
      <div className="relative max-w-6xl mx-auto px-6 py-20 flex items-center min-h-[520px]" data-id="element-3671">
        <div className={`grid ${image ? 'grid-cols-2' : 'grid-cols-1'} gap-12 items-center w-full`} data-id="element-3672">
          {/* Content */}
          <div className={image ? '' : 'max-w-3xl mx-auto text-center'} data-id="element-3673">
            <div className={`inline-flex items-center gap-2 px-3 py-1 ${eyebrowBg} backdrop-blur-sm ${eyebrowText} rounded-full text-sm font-medium mb-6`} data-id="element-3674">
              {eyebrow}
            </div>
            <h1 className={`text-5xl font-bold ${textColor} mb-6 leading-tight`} data-id="element-3675">
              {title}
            </h1>
            <p className={`text-xl ${subtitleColor} mb-8 leading-relaxed`} data-id="element-3676">
              {subtitle}
            </p>
            <div className={`flex items-center gap-4 ${image ? '' : 'justify-center'}`} data-id="element-3677">
              <Link to={primaryCta.href} className="px-6 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-50 font-medium shadow-lg" data-id="element-3678">
                {primaryCta.text}
              </Link>
              {secondaryCta && <Link to={secondaryCta.href} className={`px-6 py-3 border-2 ${isDark ? 'border-white text-white hover:bg-white/10' : 'border-gray-900 text-gray-900 hover:bg-gray-900/10'} rounded-lg font-medium`} data-id="element-3679">
                  {secondaryCta.text}
                </Link>}
            </div>
          </div>
          {/* Image */}
          {image && <div className="relative" data-id="element-3680">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-8 aspect-video flex items-center justify-center" data-id="element-3681">
                <img src={image} alt={title} className="w-full h-full object-cover rounded-lg" data-id="element-3682" />
              </div>
            </div>}
        </div>
      </div>
    </section>;
}